import { ApolloError, useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  BreadcrumbArray,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  CurrencyFlagEmoji,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  LabelPill,
  MfaFlow,
  NumericFormatInput,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Spinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  currencyCodeToSingularName,
  currencySchema,
  fxAllowedCurrencies,
  getCountryLists,
  knownProviderIds,
  NotFoundError,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import countries from "i18n-iso-countries";
import { Check, X } from "lucide-react";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { OpenCurrencycloudAccountDialog } from "~/components/Currencycloud/OpenCurrencycloudAccountDialog";
import { EntityScreeningCard } from "~/components/EntityScreeningCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import type {
  AdminAvailableCurrencycloudAccountFragment,
  AdminBusinessEntityCurrencycloudAccountFragment,
  AdminBusinessEntityDetailMercuryOnboardingFragment,
  AdminBusinessEntityOrganisationFragment,
  BusinessEntityDetailQuery,
  Currency,
  CurrencycloudAccountComplianceInput,
  SetBusinessEntityAddressInput,
  UpdateBusinessEntityDetailInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/organisations_/$organisationId_/business-entities_/$businessEntityId",
)({
  head: () => opsHead("Business Entity"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const creditConditionsSchema = z.object({
  notionalAmount: z
    .number()
    .min(0.01, "Notional amount must be greater than 0")
    .transform((value) => new Decimal(value)),
  notionalCurrency: currencySchema,
});

const createCorpayClientSchema = z.object({
  clientCode: z.string().min(1, "Client code is required"),
  apiUserId: z.string().min(1, "API user ID is required"),
  apiSignature: z.string().min(1, "API signature is required"),
});

type CreateCorpayClientFormValues = z.infer<typeof createCorpayClientSchema>;

type CreditConditionsFormValues = {
  notionalAmount: number;
  notionalCurrency: Currency;
};

const setEburyClientCreditConditionsMutation = graphql(`
  mutation SetEburyClientCreditConditions($input: SetEburyClientCreditConditionsInput!) {
    setEburyClientCreditConditions(input: $input) {
      eburyClient {
        id
        creditConditions {
          notionalAmount
          notionalCurrency
        }
      }
    }
  }
`);

type EburyClientForCreditConditions = NonNullable<
  NonNullable<BusinessEntityDetailQuery["businessEntity"]>["eburyClient"]
>;

type CurrencycloudRequirement = { label: string; met: boolean };

function getCurrencycloudRequirements({
  kycUri,
  isUSDomiciled,
}: {
  kycUri: string | null | undefined;
  isUSDomiciled: boolean;
}): CurrencycloudRequirement[] {
  return [
    { label: "A KYC link has been set", met: Boolean(kycUri) },
    { label: "Not a US-domiciled business entity", met: !isUSDomiciled },
  ];
}

function CurrencycloudRequirements({
  requirements,
}: {
  requirements: CurrencycloudRequirement[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <Txt variant="detail-label">Requirements to open a Visa account</Txt>
      <ul className="flex flex-col gap-1">
        {requirements.map((requirement) => (
          <li key={requirement.label} className="flex items-center gap-2">
            {requirement.met ? (
              <Check className="size-4 shrink-0 text-success" />
            ) : (
              <X className="size-4 shrink-0 text-danger" />
            )}
            <Txt variant="body" tone={requirement.met ? "secondary" : "danger"}>
              {requirement.label}
            </Txt>
          </li>
        ))}
      </ul>
    </div>
  );
}

const FX_PROVIDER_TYPENAME_TO_PROVIDER_ID: Record<string, string> = {
  EburyClient: knownProviderIds.ebury,
  CurrencycloudAccount: knownProviderIds.currencycloud,
  CorpayClient: knownProviderIds.corpay,
};

const FX_PROVIDER_ID_LABEL: Record<string, string> = {
  [knownProviderIds.ebury]: "Ebury",
  [knownProviderIds.currencycloud]: "Currencycloud",
  [knownProviderIds.corpay]: "Corpay",
};

function ActiveFxProviderCard({
  businessEntityId,
  activeFxProviderId,
  onboardedFxProviders,
}: {
  businessEntityId: string;
  activeFxProviderId: string | null;
  onboardedFxProviders: { __typename: string; id: string }[];
}) {
  const [setActiveFxProvider, setActiveFxProviderResult] = useMutation(
    graphql(`
      mutation SetBusinessEntityActiveFxProvider(
        $input: SetBusinessEntityActiveFxProviderInput!
      ) {
        setBusinessEntityActiveFxProvider(input: $input) {
          businessEntity {
            id
            activeFxProvider {
              id
            }
            fxProvider {
              __typename
              id
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Active FX provider updated",
        });
      },
    },
  );

  const [selected, setSelected] = useState<string | null>(activeFxProviderId);
  useEffect(() => {
    setSelected(activeFxProviderId);
  }, [activeFxProviderId]);

  const options = onboardedFxProviders
    .map((p) => FX_PROVIDER_TYPENAME_TO_PROVIDER_ID[p.__typename])
    .filter((id): id is string => id !== undefined);

  if (options.length === 0) {
    return null;
  }

  const isUnchanged = selected === activeFxProviderId;

  const handleSave = () => {
    if (!selected) {
      return;
    }
    setActiveFxProvider({
      variables: {
        input: { businessEntityId, activeFxProviderId: selected },
      },
    });
  };

  return (
    <Card>
      <CardContent className="space-y-4">
        <Txt variant="body" tone="secondary">
          The provider new FX quotes and trades are originated against. An
          entity can be onboarded with multiple providers (e.g. while
          migrating); switch the active one here without off-boarding the
          previous provider.
        </Txt>
        <div className="min-w-[300px]">
          <Select
            value={selected ?? undefined}
            onValueChange={(value) => setSelected(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select active FX provider…" />
            </SelectTrigger>
            <SelectContent>
              {options.map((providerId) => (
                <SelectItem key={providerId} value={providerId}>
                  {FX_PROVIDER_ID_LABEL[providerId] ?? providerId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          disabled={isUnchanged || setActiveFxProviderResult.loading}
          loading={setActiveFxProviderResult.loading}
          onClick={handleSave}
        >
          Save
        </Button>
      </CardFooter>
    </Card>
  );
}

function DeleteBusinessEntityCard({
  businessEntityId,
  businessEntityName,
  organisationId,
}: {
  businessEntityId: string;
  businessEntityName: string;
  organisationId: string;
}) {
  const navigate = useNavigate();
  const [deleteBusinessEntity, deleteBusinessEntityResult] = useMutation(
    graphql(`
      mutation DeleteBusinessEntity($input: DeleteBusinessEntityInput!) {
        deleteBusinessEntity(input: $input) {
          deletedId
        }
      }
    `),
    {
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            __typename: "BusinessEntity",
            id: businessEntityId,
          }),
        });
        cache.gc();
      },
    },
  );

  const handleDelete = async () => {
    try {
      await deleteBusinessEntity({
        variables: { input: { businessEntityId } },
      });
      toast({
        kind: "success",
        autoHide: true,
        title: `Business entity "${businessEntityName}" deleted`,
      });
      await navigate({
        to: "/organisations/$organisationId",
        params: { organisationId },
      });
    } catch (e) {
      const description =
        e instanceof ApolloError ? e.graphQLErrors?.[0]?.message : undefined;
      toast({
        kind: "error",
        autoHide: false,
        title: "Error deleting business entity",
        description,
      });
    }
  };

  return (
    <Card className="border border-danger bg-danger-muted/40 p-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2">
          <Txt variant="card-title" tone="danger">
            Danger zone
          </Txt>
          <Txt variant="body" tone="danger">
            Permanently deletes this business entity. Blocked while it has any
            assets, beneficiary accounts, FX providers, FX trades, external
            connections or other linked records.
          </Txt>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="destructive">Delete business entity</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete business entity</DialogTitle>
              <DialogDescription>
                This permanently deletes "{businessEntityName}" and cannot be
                undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex items-center gap-2">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                disabled={deleteBusinessEntityResult.loading}
                loading={deleteBusinessEntityResult.loading}
                onClick={() => void handleDelete()}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
}

function EburyClientCreditConditionsForm({
  eburyClient,
  showClientId,
}: {
  eburyClient: EburyClientForCreditConditions;
  showClientId: boolean;
}) {
  const existingCreditConditions = eburyClient.creditConditions;
  const defaultValues = useMemo(
    () => ({
      notionalAmount: existingCreditConditions
        ? existingCreditConditions.notionalAmount.toNumber()
        : 0,
      notionalCurrency: existingCreditConditions?.notionalCurrency ?? "GBP",
    }),
    [existingCreditConditions],
  );

  const [setEburyClientCreditConditions, setEburyClientCreditConditionsResult] =
    useMutation(setEburyClientCreditConditionsMutation, {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Ebury credit conditions updated",
        });
      },
    });

  const creditConditionsForm = useAppForm({
    defaultValues: defaultValues as CreditConditionsFormValues,
    validators: {
      onSubmit: creditConditionsSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = creditConditionsSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Invalid credit conditions",
        });
        return;
      }

      try {
        await setEburyClientCreditConditions({
          variables: {
            input: {
              eburyClientId: eburyClient.id,
              notionalAmount: parsed.data.notionalAmount,
              notionalCurrency: parsed.data.notionalCurrency,
            },
          },
        });
      } catch (error) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update Ebury credit conditions",
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
  });

  return (
    <div>
      <h4 className="mb-2 font-medium text-base">
        Credit Conditions{showClientId ? ` (${eburyClient.id})` : ""}
      </h4>
      <creditConditionsForm.AppForm>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void creditConditionsForm.handleSubmit();
          }}
        >
          <input type="hidden" name="eburyClientId" value={eburyClient.id} />
          <div className="flex flex-col gap-4">
            <div className="grid w-1/2 grid-cols-2 gap-4">
              <creditConditionsForm.AppField name="notionalAmount">
                {(field) => (
                  <div>
                    <span>Notional amount</span>
                    <NumericFormatInput
                      value={field.state.value}
                      onChange={(value) =>
                        value.target.value &&
                        field.handleChange(value.target.value.toNumber())
                      }
                      required
                      thousandSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="mt-1 text-danger text-sm">
                        {field.state.meta.errors
                          .map((err) => err?.message)
                          .join(", ")}
                      </p>
                    )}
                  </div>
                )}
              </creditConditionsForm.AppField>

              <creditConditionsForm.AppField name="notionalCurrency">
                {(field) => (
                  <field.SelectField
                    label="Notional currency"
                    errorVisibilityMode="onBlur"
                  >
                    {currencyValues.map((currency) => (
                      <field.SelectItem key={currency} value={currency}>
                        <div className="flex flex-row gap-2">
                          <CurrencyFlagEmoji
                            currency={currency}
                            className="rounded text-lg"
                          />
                          {currency}
                        </div>
                      </field.SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </creditConditionsForm.AppField>
            </div>

            {setEburyClientCreditConditionsResult.error && (
              <Alert
                kind="error"
                description={setEburyClientCreditConditionsResult.error.message}
              />
            )}

            <div className="flex justify-end">
              <Button
                disabled={setEburyClientCreditConditionsResult.loading}
                loading={setEburyClientCreditConditionsResult.loading}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </creditConditionsForm.AppForm>
    </div>
  );
}

function View() {
  const params = Route.useParams();
  const [eburyClientId, setEburyClientId] = useState("");

  const { data, error } = useSuspenseQuery(
    graphql(`
      query BusinessEntityDetail($id: ID!) {
        businessEntity(id: $id) {
          id
          ...AdminBusinessEntityDetail
        }
        ... @defer {
          eburyClients {
            id
            clientName
            businessEntity {
              id
            }
          }
        }
        ... @defer {
          currencycloudAccounts {
            id
            ...AdminAvailableCurrencycloudAccount
          }
        }
      }

      fragment AdminBusinessEntityDetail on BusinessEntity {
        id
        name
        abn
        currency
        domiciledIn
        kycVerified
        kycUri
        netsuiteInternalCustomerId
        organisation {
          id
          ...AdminBusinessEntityOrganisation
        }
        eburyClient {
          id
          deactivatedAt
          creditConditions {
            notionalAmount
            notionalCurrency
          }
        }
        corpayClient {
          id
          clientCode
          createdAt
          updatedAt
        }
        address {
          id
          buildingNumber
          streetName
          city
          state
          postcode
          country
        }
        currencycloudAccount {
          id
          ...AdminBusinessEntityCurrencycloudAccount
        }
        activeFxProvider {
          id
        }
        onboardedFxProviders {
          __typename
          id
        }
        tradeableCurrencies
        screeningResults {
          ...EntityScreeningCardFields
        }
        ...AdminBusinessEntityDetailMercuryOnboarding
      }

      fragment AdminBusinessEntityDetailMercuryOnboarding on BusinessEntity {
        id
        isUSDomiciled
        organisation {
          id
        }
        address {
          id
        }
        mercuryConnections {
          id
          status
        }
      }

      fragment AdminBusinessEntityOrganisation on Organisation {
        id
        name
        activityStatementFormat
        memberships {
          id
          userProfile {
            id
            avatarUrl
            friendlyName
          }
        }
      }

      fragment AdminBusinessEntityCurrencycloudAccount on CurrencycloudAccount {
        id
        ... @defer {
          id
          accountName
          shortReference
          status
          createdAt
          defaultOnBehalfOfContact {
            id
            friendlyName
            userProfile {
              id
              friendlyName
            }
          }
          assets {
            id
            currency
            ... on FXAsset {
              currencycloudBalance {
                id
                amount
              }
            }
          }
        }
      }

      fragment AdminAvailableCurrencycloudAccount on CurrencycloudAccount {
        id
        accountName
        shortReference
        businessEntity {
          id
        }
      }
    `),
    { variables: { id: params.businessEntityId ?? "" } },
  );
  const businessEntity = data?.businessEntity;
  if (
    !businessEntity?.organisation ||
    businessEntity.organisation.id !== params.organisationId
  ) {
    throw new NotFoundError();
  }

  useOpsTitle(
    `${data.businessEntity?.organisation?.name} - ${data.businessEntity?.name}`,
  );

  useEffect(() => {
    if (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Error",
        description: error.message,
      });
    }
  }, [error]);

  const currencycloudAccountErrors = error?.graphQLErrors.filter(
    (g) =>
      g.path?.at(0) === "businessEntity" &&
      g.path?.at(1) === "currencycloudAccount",
  );

  const [
    overrideCurrencycloudEligibility,
    setOverrideCurrencycloudEligibility,
  ] = useState(false);

  const currencycloudRequirements = getCurrencycloudRequirements({
    kycUri: businessEntity.kycUri,
    isUSDomiciled: businessEntity.isUSDomiciled,
  });
  const currencycloudRequirementsMet = currencycloudRequirements.every(
    (requirement) => requirement.met,
  );

  // The override checkbox lets operators proceed despite unmet requirements.
  const canOpenCurrencycloudAccount =
    currencycloudRequirementsMet || overrideCurrencycloudEligibility;

  const [updateDetails, updateDetailsResult] = useMutation(
    graphql(`
      mutation UpdateBusinessEntityDetail($input: UpdateBusinessEntityDetailInput!) {
        updateBusinessEntityDetail(input: $input) {
          businessEntity {
            id
            name
            abn
            currency
            domiciledIn
            kycVerified
            kycUri
            netsuiteInternalCustomerId
            ...AdminBusinessEntityDetailMercuryOnboarding
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Details updated",
        });
      },
    },
  );

  const [linkEburyClient, linkEburyClientResult] = useMutation(
    graphql(`
      mutation LinkEburyClient($input: LinkEburyClientInput!) {
        linkEburyClient(input: $input) {
          businessEntity {
            id
            eburyClient {
              id
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        setEburyClientId("");
      },
    },
  );

  const [deactivateEburyClient, deactivateEburyClientResult] = useMutation(
    graphql(`
      mutation DeactivateEburyClient($input: DeactivateEburyClientInput!) {
        deactivateEburyClient(input: $input) {
          businessEntity {
            id
            eburyClient {
              id
            }
          }
        }
      }
    `),
  );

  const [setAddress, setAddressResult] = useMutation(
    graphql(`
      mutation SetBusinessEntityAddress($input: SetBusinessEntityAddressInput!) {
        setBusinessEntityAddress(input: $input) {
          businessEntity {
            id
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Business entity address updated",
        });
      },
    },
  );

  const [createCurrencycloudAccount, createCurrencycloudAccountResult] =
    useMutation(
      graphql(`
        mutation CreateCurrencycloudAccount($input: CreateCurrencycloudAccountInput!) {
          createCurrencycloudAccount(input: $input) {
            businessEntity {
              id
              currencycloudAccount {
                id
                ...AdminBusinessEntityCurrencycloudAccount
              }
            }
          }
        }
      `),
      {
        onCompleted: () => {
          toast({
            kind: "success",
            autoHide: true,
            title: "Visa account created",
          });
        },
      },
    );

  const [createCorpayClient, createCorpayClientResult] = useMutation(
    graphql(`
      mutation CreateCorpayClient($input: CreateCorpayClientInput!) {
        createCorpayClient(input: $input) {
          businessEntity {
            id
            corpayClient {
              id
              clientCode
              createdAt
              updatedAt
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Corpay client created",
        });
      },
      onError: (error) => {
        const description =
          error.graphQLErrors[0].message ??
          "Something went wrong. Please try again later.";
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to create Corpay client",
          description,
        });
      },
    },
  );

  const { commonCountriesList, uncommonCountriesList } = getCountryLists();

  // Remix will not remount the component when navigating between business entities. Manually reset fields on change.
  const previousBusinessEntityId = useRef(params.businessEntityId);
  useEffect(() => {
    if (previousBusinessEntityId.current !== params.businessEntityId) {
      setEburyClientId("");
      linkEburyClientResult.reset?.();
      createCurrencycloudAccountResult.reset?.();
      createCorpayClientResult.reset?.();
      previousBusinessEntityId.current = params.businessEntityId;
    }
  });

  const requiresNetSuiteCustomerId =
    businessEntity.organisation?.activityStatementFormat === "NETSUITE";
  const detailsForm = useAppForm({
    defaultValues: {
      name: data.businessEntity?.name ?? "",
      abn: data.businessEntity?.abn ?? undefined,
      currency: data.businessEntity?.currency ?? undefined,
      domiciledIn: data.businessEntity?.domiciledIn,
      kycVerified: data.businessEntity?.kycVerified ?? false,
      kycUri: data.businessEntity?.kycUri ?? "",
      netsuiteInternalCustomerId:
        data.businessEntity?.netsuiteInternalCustomerId ?? "",
    } as Omit<UpdateBusinessEntityDetailInput, "businessEntityId">,
    validators: {
      onSubmit: z
        .object({
          name: z.string().min(1),
          abn: z.string().optional(),
          currency: currencySchema.optional(),
          domiciledIn: z.string().nullish(),
          kycVerified: z.boolean().nullish(),
          kycUri: z.union([z.string().url(), z.literal("")]).nullish(),
          netsuiteInternalCustomerId: z.string().optional(),
        })
        .superRefine((value, ctx) => {
          if (
            requiresNetSuiteCustomerId &&
            !value.netsuiteInternalCustomerId?.trim()
          ) {
            ctx.addIssue({
              path: ["netsuiteInternalCustomerId"],
              code: "custom",
              message:
                "This organisation is configured to use NetSuite activity statements. A NetSuite internal customer ID is required for all business entities.",
            });
          }
        }),
    },
    onSubmit: async ({ value: formFields }) => {
      updateDetails({
        variables: {
          input: {
            businessEntityId: businessEntity.id,
            ...formFields,
            abn: formFields.abn?.trim() || null,
            currency: formFields.currency || undefined,
            domiciledIn: formFields.domiciledIn,
            kycUri: formFields.kycUri?.trim() ? formFields.kycUri.trim() : null,
            netsuiteInternalCustomerId:
              formFields.netsuiteInternalCustomerId?.trim(),
          },
        },
      });
    },
  });

  const addressForm = useAppForm({
    defaultValues: {
      buildingNumber: data.businessEntity?.address?.buildingNumber ?? "",
      streetName: data.businessEntity?.address?.streetName ?? "",
      city: data.businessEntity?.address?.city ?? "",
      state: data.businessEntity?.address?.state ?? "",
      postcode: data.businessEntity?.address?.postcode ?? "",
      country:
        [...commonCountriesList, ...uncommonCountriesList].find(
          ({ label }) => label === data.businessEntity?.address?.country,
        )?.value ?? "AUS",
    } as Omit<SetBusinessEntityAddressInput, "businessEntityId">,
    validators: {
      onSubmit: z.object({
        buildingNumber: z.string(),
        streetName: z.string(),
        city: z.string(),
        state: z.string(),
        postcode: z.string(),
        country: z.string().refine(countries.isValid),
      }),
    },
    onSubmit: async ({ value: formFields }) => {
      setAddress({
        variables: {
          input: {
            businessEntityId: businessEntity.id,
            ...formFields,
          },
        },
      });
    },
  });

  const handleAddEburyClient = () => {
    if (!eburyClientId.trim()) {
      throw new Error("Ebury Client ID is required");
    }
    linkEburyClient({
      variables: {
        input: {
          businessEntityId: businessEntity.id,
          eburyClientId: eburyClientId.trim(),
        },
      },
    });
  };

  const handleDeactivateEburyClient = async () => {
    try {
      await deactivateEburyClient({
        variables: {
          input: {
            businessEntityId: businessEntity.id,
          },
        },
      });
      toast({
        kind: "success",
        autoHide: true,
        title: "Ebury deactivated for this business entity",
      });
    } catch (e) {
      const description =
        e instanceof ApolloError ? e.graphQLErrors?.[0]?.message : undefined;
      toast({
        kind: "error",
        autoHide: false,
        title: "Error deactivating Ebury",
        description,
      });
    }
  };

  const [openAccountDialogOpen, setOpenAccountDialogOpen] = useState(false);

  const handleCreateCurrencycloudAccount = async (
    compliance: CurrencycloudAccountComplianceInput,
  ) => {
    const result = await createCurrencycloudAccount({
      variables: {
        input: {
          businessEntityId: businessEntity.id,
          overrideEligibilityRequirements: overrideCurrencycloudEligibility,
          compliance,
        },
      },
    }).catch(() => null);
    if (result) {
      setOpenAccountDialogOpen(false);
    }
  };

  const entityCurrencycloudAccount =
    data.businessEntity?.currencycloudAccount ?? null;

  const entityEburyClient = data.businessEntity?.eburyClient ?? null;
  const isEburyDeactivated = entityEburyClient?.deactivatedAt != null;
  const entityCorpayClient = data.businessEntity?.corpayClient ?? null;

  const unlinkedEburyClients = (data.eburyClients ?? []).filter(
    (e) => e.businessEntity === null,
  );

  const corpayClientForm = useAppForm({
    defaultValues: {
      clientCode: "",
      apiUserId: "",
      apiSignature: "",
    } as CreateCorpayClientFormValues,
    validators: {
      onSubmit: createCorpayClientSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = createCorpayClientSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Invalid Corpay client details",
        });
        return;
      }

      await createCorpayClient({
        variables: {
          input: {
            businessEntityId: businessEntity.id,
            clientCode: parsed.data.clientCode,
            apiUserId: parsed.data.apiUserId,
            apiSignature: parsed.data.apiSignature,
          },
        },
      });
      corpayClientForm.reset();
    },
  });

  return (
    <AdminPageContainer>
      <BreadcrumbArray
        stack={[
          {
            to: "/organisations",
            title: "Organisations",
          },
          {
            to: `/organisations/${data?.businessEntity?.organisation?.id}`,
            title: data.businessEntity?.organisation?.name,
          },
          {
            title: data?.businessEntity?.name,
          },
        ]}
      />
      <AdminPageHeader
        title={`${data.businessEntity?.organisation?.name} - ${data.businessEntity?.name}`}
      />
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="mb-4">
              <Txt variant="section-title">Business Entity</Txt>
            </div>
            <div className="rounded-lg border bg-primary p-4 text-sm shadow-surface">
              <detailsForm.AppForm>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void detailsForm.handleSubmit();
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <detailsForm.AppField name="name">
                      {(field) => <field.InputField label="Name" />}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="abn">
                      {(field) => <field.InputField label="ABN" />}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="currency">
                      {(field) => (
                        <field.SelectField label="Currency">
                          {currencyValues.map((c) => (
                            <field.SelectItem value={c} key={c}>
                              <div className="flex flex-row gap-2">
                                <CurrencyFlagEmoji
                                  currency={c}
                                  className="rounded text-lg"
                                />
                                {c}
                              </div>
                            </field.SelectItem>
                          ))}
                        </field.SelectField>
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="domiciledIn">
                      {(field) => (
                        <field.SelectField label="Domiciled In">
                          {commonCountriesList.map(({ value, label }) => (
                            <SelectItem value={value} key={`common-${value}`}>
                              {label}
                            </SelectItem>
                          ))}
                          <SelectSeparator />
                          {uncommonCountriesList.map(({ value, label }) => (
                            <SelectItem value={value} key={`uncommon-${value}`}>
                              {label}
                            </SelectItem>
                          ))}
                        </field.SelectField>
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="netsuiteInternalCustomerId">
                      {(field) => (
                        <field.InputField
                          label="NetSuite internal customer ID"
                          placeholder={
                            requiresNetSuiteCustomerId
                              ? "Required for this organisation"
                              : undefined
                          }
                        />
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="kycUri">
                      {(field) => (
                        <div className="flex flex-col gap-1">
                          <field.InputField
                            label="KYC link"
                            placeholder="https://aiprise.com/..."
                          />
                          {businessEntity.kycUri && (
                            <a
                              href={businessEntity.kycUri}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-interactive text-sm underline"
                            >
                              Open KYC link
                            </a>
                          )}
                        </div>
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="kycVerified">
                      {(field) => <field.SwitchField label="KYC verified" />}
                    </detailsForm.AppField>

                    <div className="flex flex-col items-end">
                      <Button disabled={updateDetailsResult.loading}>
                        Save
                      </Button>
                    </div>
                  </div>
                </form>
              </detailsForm.AppForm>
            </div>
          </div>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">Address</Txt>
            </div>
            <div className="rounded-lg border bg-primary p-4 text-sm shadow-surface">
              <addressForm.AppForm>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void addressForm.handleSubmit();
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                      <addressForm.AppField name="buildingNumber">
                        {(field) => (
                          <field.InputField label="Building number" />
                        )}
                      </addressForm.AppField>
                      <addressForm.AppField name="streetName">
                        {(field) => <field.InputField label="Street name" />}
                      </addressForm.AppField>
                      <addressForm.AppField name="city">
                        {(field) => <field.InputField label="City" />}
                      </addressForm.AppField>
                    </div>

                    <div className="flex gap-4">
                      <addressForm.AppField name="state">
                        {(field) => <field.InputField label="State" />}
                      </addressForm.AppField>
                      <addressForm.AppField name="postcode">
                        {(field) => <field.InputField label="Postcode" />}
                      </addressForm.AppField>
                      <addressForm.AppField name="country">
                        {(field) => (
                          <field.SelectField
                            label="Country"
                            placeholder="Select a country"
                          >
                            {commonCountriesList.map(({ value, label }) => (
                              <SelectItem value={value} key={`common-${value}`}>
                                {label}
                              </SelectItem>
                            ))}
                            <SelectSeparator />
                            {uncommonCountriesList.map(({ value, label }) => (
                              <SelectItem
                                value={value}
                                key={`uncommon-${value}`}
                              >
                                {label}
                              </SelectItem>
                            ))}
                          </field.SelectField>
                        )}
                      </addressForm.AppField>
                    </div>
                    <div className="flex flex-col items-end">
                      <Button disabled={setAddressResult.loading}>Save</Button>
                    </div>
                  </div>
                </form>
              </addressForm.AppForm>
            </div>
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 gap-8">
          <div className="flex flex-col gap-4">
            <Txt variant="section-title">Active FX provider</Txt>

            <ActiveFxProviderCard
              businessEntityId={businessEntity.id}
              activeFxProviderId={businessEntity.activeFxProvider?.id ?? null}
              onboardedFxProviders={businessEntity.onboardedFxProviders ?? []}
            />
          </div>

          <div className="flex flex-col gap-4">
            <Txt variant="section-title">Ebury</Txt>
            <Card>
              {entityEburyClient != null ? (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <p className="text-primary">
                      {isEburyDeactivated
                        ? "The following Ebury client ID was associated with this business entity and has been deactivated:"
                        : "The following Ebury client ID is associated with this business entity:"}
                    </p>
                    {isEburyDeactivated && (
                      <LabelPill variant="warning" label="Deactivated" />
                    )}
                  </div>
                  <div className="rounded border bg-neutral-muted p-3">
                    <Txt variant="mono">{entityEburyClient.id}</Txt>
                  </div>
                </div>
              ) : (
                <p className="text-secondary italic">
                  No Ebury client connections configured
                </p>
              )}

              {entityEburyClient != null && !isEburyDeactivated && (
                <div className="mt-4 border-grid border-t pt-4">
                  <EburyClientCreditConditionsForm
                    eburyClient={entityEburyClient}
                    showClientId={false}
                  />
                </div>
              )}

              {entityEburyClient != null && !isEburyDeactivated && (
                <div className="mt-4 rounded-lg border border-danger bg-danger-muted/40 p-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-1">
                      <Txt variant="card-title" tone="danger">
                        Danger zone
                      </Txt>
                      <Txt variant="body" tone="danger">
                        Deactivating Ebury unlinks this client from the business
                        entity and makes its Ebury FX accounts inactive. The
                        entity remains onboarded at Ebury. Blocked while there
                        are open trades, non-zero balances, or pending payments.
                      </Txt>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="destructive">Deactivate Ebury</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <MfaFlow
                          promptVariant="dialog-content"
                          onResolve={handleDeactivateEburyClient}
                        >
                          {(requestMfa) => (
                            <>
                              <DialogHeader>
                                <DialogTitle>Deactivate Ebury</DialogTitle>
                                <DialogDescription>
                                  This unlinks Ebury client{" "}
                                  {entityEburyClient.id} and disables its FX
                                  accounts for this business entity.
                                </DialogDescription>
                              </DialogHeader>
                              <form
                                className="flex flex-col gap-6"
                                onSubmit={async (event) => {
                                  event.preventDefault();
                                  await requestMfa();
                                }}
                              >
                                <DialogFooter className="flex items-center gap-2">
                                  <DialogClose asChild>
                                    <Button type="button" variant="secondary">
                                      Cancel
                                    </Button>
                                  </DialogClose>
                                  <Button
                                    type="submit"
                                    variant="destructive"
                                    disabled={
                                      deactivateEburyClientResult.loading
                                    }
                                  >
                                    {deactivateEburyClientResult.loading
                                      ? "Deactivating..."
                                      : "Deactivate Ebury"}
                                  </Button>
                                </DialogFooter>
                              </form>
                            </>
                          )}
                        </MfaFlow>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              )}

              {entityEburyClient == null && (
                <div className="mt-4 border-grid border-t pt-4">
                  <h4 className="mb-2 font-medium">Add Ebury Client</h4>
                  <div className="flex items-end gap-2">
                    <div className="flex flex-grow flex-col gap-4">
                      {linkEburyClientResult.error && (
                        <Alert
                          kind="error"
                          description={linkEburyClientResult.error.message}
                          className="mt-2"
                        />
                      )}

                      {data.eburyClients === undefined ? (
                        <div className="flex items-center gap-2 text-xs">
                          <Spinner size="md" />
                          Loading available Ebury clients
                        </div>
                      ) : (
                        <Select
                          disabled={
                            unlinkedEburyClients.length === 0 ||
                            linkEburyClientResult.loading
                          }
                          onValueChange={setEburyClientId}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                unlinkedEburyClients.length
                                  ? "Select Ebury client"
                                  : "No available Ebury clients to link"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {unlinkedEburyClients.map((eburyClient) => (
                              <SelectItem
                                key={eburyClient.id}
                                value={eburyClient.id}
                              >
                                {eburyClient.id} - {eburyClient.clientName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <Button
                      onClick={handleAddEburyClient}
                      disabled={
                        linkEburyClientResult.loading || !eburyClientId.trim()
                      }
                      loading={linkEburyClientResult.loading}
                      className="mb-[1px]"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Txt variant="section-title">Corpay</Txt>
            <Card>
              {entityCorpayClient ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Txt variant="detail-label">Client ID</Txt>
                    <Txt variant="mono">{entityCorpayClient.id}</Txt>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Txt variant="detail-label">Client code</Txt>
                    <Txt variant="mono">{entityCorpayClient.clientCode}</Txt>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Txt variant="detail-label">Created</Txt>
                    <Txt variant="body">
                      <DateUI date={new Date(entityCorpayClient.createdAt)} />
                    </Txt>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Txt variant="detail-label">Updated</Txt>
                    <Txt variant="body">
                      <DateUI date={new Date(entityCorpayClient.updatedAt)} />
                    </Txt>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <p className="text-secondary italic">
                    No Corpay client configured
                  </p>

                  <div className="border-grid border-t pt-4">
                    <corpayClientForm.AppForm>
                      <form
                        onSubmit={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void corpayClientForm.handleSubmit();
                        }}
                      >
                        <div className="flex flex-col gap-4">
                          <corpayClientForm.AppField name="clientCode">
                            {(field) => (
                              <field.InputField label="Client code" />
                            )}
                          </corpayClientForm.AppField>

                          <corpayClientForm.AppField name="apiUserId">
                            {(field) => (
                              <field.InputField label="API user ID" />
                            )}
                          </corpayClientForm.AppField>

                          <corpayClientForm.AppField name="apiSignature">
                            {(field) => (
                              <field.TextareaField label="API signature" />
                            )}
                          </corpayClientForm.AppField>

                          <div className="flex justify-end">
                            <Button
                              disabled={createCorpayClientResult.loading}
                              loading={createCorpayClientResult.loading}
                              type="submit"
                            >
                              Create Corpay client
                            </Button>
                          </div>
                        </div>
                      </form>
                    </corpayClientForm.AppForm>
                  </div>
                </div>
              )}
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Txt variant="section-title">Visa (Currencycloud)</Txt>

            {entityCurrencycloudAccount !== null && (
              <CurrencycloudAccountCard
                account={entityCurrencycloudAccount}
                organisation={businessEntity.organisation}
                businessEntityId={businessEntity.id}
              />
            )}

            {!!currencycloudAccountErrors?.length && (
              <Alert
                kind="error"
                description={
                  "Could not load Visa accounts: " +
                  Array.from(
                    new Set(currencycloudAccountErrors.map((c) => c.message)),
                  ).join(", ")
                }
                className="mt-2"
              />
            )}

            {!currencycloudAccountErrors?.length &&
              entityCurrencycloudAccount === null && (
                <div className="grid grid-cols-2 gap-8">
                  <div className="flex flex-col gap-4 rounded-lg border bg-primary p-4 text-sm shadow-surface">
                    <div className="font-semibold">Open new Visa account</div>

                    <div className="flex flex-col gap-3">
                      <CurrencycloudRequirements
                        requirements={currencycloudRequirements}
                      />
                      {!currencycloudRequirementsMet && (
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={overrideCurrencycloudEligibility}
                            onCheckedChange={(checked) =>
                              setOverrideCurrencycloudEligibility(
                                checked === true,
                              )
                            }
                          />
                          Override requirements and open account anyway
                        </label>
                      )}
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            createCurrencycloudAccountResult.reset?.();
                            setOpenAccountDialogOpen(true);
                          }}
                          disabled={!canOpenCurrencycloudAccount}
                        >
                          Open account
                        </Button>
                      </div>
                    </div>
                    <OpenCurrencycloudAccountDialog
                      open={openAccountDialogOpen}
                      onOpenChange={setOpenAccountDialogOpen}
                      onSubmit={handleCreateCurrencycloudAccount}
                      errorMessage={
                        createCurrencycloudAccountResult.error?.message
                      }
                    />
                  </div>

                  <CurrencycloudLinkSection
                    businessEntityId={businessEntity.id}
                    currencycloudAccounts={data.currencycloudAccounts}
                    requirements={currencycloudRequirements}
                  />
                </div>
              )}
          </div>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">Mercury onboarding</Txt>
            </div>
            <MercuryOnboardingSection businessEntity={businessEntity} />
          </div>
        </div>

        <EntityScreeningCard
          screeningResults={data.businessEntity?.screeningResults ?? []}
          entityId={{ businessEntityId: params.businessEntityId }}
          entityName={data.businessEntity?.name ?? ""}
        />

        <DeleteBusinessEntityCard
          businessEntityId={businessEntity.id}
          businessEntityName={businessEntity.name}
          organisationId={businessEntity.organisation.id}
        />
      </div>
    </AdminPageContainer>
  );
}

type OrganisationMembership = NonNullable<
  AdminBusinessEntityOrganisationFragment["memberships"]
>[number];

function CurrencycloudAccountCard({
  account,
  organisation,
  businessEntityId,
}: {
  account: AdminBusinessEntityCurrencycloudAccountFragment;
  organisation?: AdminBusinessEntityOrganisationFragment | null;
  businessEntityId: string;
}) {
  const [deactivateCurrencycloudAccount, deactivateCurrencycloudAccountResult] =
    useMutation(
      graphql(`
        mutation DeactivateCurrencycloudAccount($input: DeactivateCurrencycloudAccountInput!) {
          deactivateCurrencycloudAccount(input: $input) {
            businessEntity {
              id
              currencycloudAccount {
                id
              }
            }
          }
        }
      `),
    );

  const handleDeactivate = async () => {
    try {
      await deactivateCurrencycloudAccount({
        variables: { input: { businessEntityId } },
      });
      toast({
        kind: "success",
        autoHide: true,
        title: "Visa account deactivated for this business entity",
      });
    } catch (e) {
      const description =
        e instanceof ApolloError ? e.graphQLErrors?.[0]?.message : undefined;
      toast({
        kind: "error",
        autoHide: false,
        title: "Error deactivating Visa account",
        description,
      });
    }
  };

  const [
    setCurrencycloudAccountDefaultContact,
    setCurrencycloudAccountDefaultContactResult,
  ] = useMutation(
    graphql(
      `mutation AdminBusinessEntitySetCurrencycloudAccountDefaultContact($input: SetCurrencycloudAccountDefaultContactInput!) {
        setCurrencycloudAccountDefaultContact(input: $input) {
          currencycloudAccount {
            id
            ...AdminBusinessEntityCurrencycloudAccount
          }
        }
      }`,
    ),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Visa account updated",
        });
      },
      onError: (error) => {
        const description =
          error.graphQLErrors[0].message ??
          "Something went wrong. Please try again later.";
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update Visa account",
          description,
        });
      },
    },
  );

  const defaultOnBehalfOfUserProfile =
    account.defaultOnBehalfOfContact?.userProfile;

  const [selectedUserProfileId, setSelectedUserProfileId] = useState(
    defaultOnBehalfOfUserProfile?.id ?? null,
  );

  useEffect(() => {
    if (account.defaultOnBehalfOfContact !== undefined) {
      setSelectedUserProfileId(defaultOnBehalfOfUserProfile?.id ?? null);
    }
  }, [account.defaultOnBehalfOfContact, defaultOnBehalfOfUserProfile?.id]);

  const isUnchanged =
    selectedUserProfileId === (defaultOnBehalfOfUserProfile?.id ?? null);

  const organisationMemberships = organisation?.memberships;
  const contactOptions = useMemo(() => {
    const orgUsers = [...(organisationMemberships ?? [])]
      .filter(
        (
          v,
        ): v is OrganisationMembership & {
          userProfile: NonNullable<OrganisationMembership["userProfile"]>;
        } => !!v.userProfile,
      )
      .map((v) => v.userProfile)
      .sort((a, b) => {
        const nameA = a.friendlyName ?? "";
        const nameB = b.friendlyName ?? "";
        return nameA.localeCompare(nameB);
      });

    return orgUsers;
  }, [organisationMemberships]);

  const handleSave = () => {
    setCurrencycloudAccountDefaultContact({
      variables: {
        input: {
          currencycloudAccountId: account.id,
          userProfileId: selectedUserProfileId,
        },
      },
    });
  };

  return (
    <Card className="border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Txt variant="section-title">
            Visa client account:{" "}
            <Link
              to={`/currencycloud/accounts/${account.id}`}
              className="text-primary-interactive underline"
            >
              {account.accountName ?? account.shortReference ?? account.id}
            </Link>
          </Txt>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {!account.defaultOnBehalfOfContact && (
          <Alert
            kind="warning"
            description="A contact must be assigned before this account can be used for transactions. Per Visa's requirements, each account must have an authorized contact to process payments."
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Account ID</Txt>
            <Txt variant="mono">{account.id}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Ref</Txt>
            <Txt variant="mono">{account.shortReference ?? "Loading..."}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Account Name</Txt>
            <Txt variant="body">{account.accountName ?? "Loading..."}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Status</Txt>
            <Txt variant="body">{account.status ?? "Loading..."}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Created</Txt>
            <Txt variant="body">
              {account.createdAt ? (
                <DateUI date={new Date(account.createdAt)} />
              ) : (
                "Loading..."
              )}
            </Txt>
          </div>

          {organisation && (
            <div className="flex flex-col gap-2">
              <Txt variant="detail-label">Contact</Txt>

              <div className="min-w-[300px]">
                {account.defaultOnBehalfOfContact === undefined ? (
                  "Loading..."
                ) : (
                  <Select
                    value={selectedUserProfileId ?? undefined}
                    onValueChange={(value) => setSelectedUserProfileId(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select contact…" />
                    </SelectTrigger>

                    <SelectContent>
                      {contactOptions.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          <div className="flex items-center gap-2">
                            {m.avatarUrl && (
                              <img
                                src={m.avatarUrl}
                                alt={m.friendlyName ?? undefined}
                                className="h-5 w-5 rounded-full"
                              />
                            )}
                            <Txt variant="body">{m.friendlyName}</Txt>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}
        </div>

        <CardFooter className="flex justify-end">
          <Button
            disabled={
              isUnchanged || setCurrencycloudAccountDefaultContactResult.loading
            }
            loading={setCurrencycloudAccountDefaultContactResult.loading}
            onClick={handleSave}
          >
            Save
          </Button>
        </CardFooter>

        <Separator />

        <div className="flex flex-col gap-4">
          <Txt variant="section-title">Open Visa FX accounts</Txt>
          <CurrencycloudAssetSelectionForm
            account={account}
            businessEntityId={businessEntityId}
          />
        </div>

        <Separator />

        <div className="rounded-lg border border-danger bg-danger-muted/40 p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <Txt variant="card-title" tone="danger">
                Danger zone
              </Txt>
              <Txt variant="body" tone="danger">
                Unlinks this Visa account from the business entity. Blocked
                while it has any contacts or assets.
              </Txt>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive">Deactivate Visa account</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Deactivate Visa account</DialogTitle>
                  <DialogDescription>
                    This unlinks Visa account {account.id} from this business
                    entity.
                  </DialogDescription>
                </DialogHeader>
                <form
                  className="flex flex-col gap-6"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await handleDeactivate();
                  }}
                >
                  <DialogFooter className="flex items-center gap-2">
                    <DialogClose asChild>
                      <Button type="button" variant="secondary">
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={deactivateCurrencycloudAccountResult.loading}
                    >
                      {deactivateCurrencycloudAccountResult.loading
                        ? "Deactivating..."
                        : "Deactivate Visa account"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const createCurrencycloudAssetsSchema = z.object({
  currencies: z
    .array(currencySchema)
    .min(1, "Please select at least one currency"),
});

type CreateCurrenycloudAssetsFormData = z.infer<
  typeof createCurrencycloudAssetsSchema
>;

function CurrencycloudAssetSelectionForm({
  account,
  businessEntityId,
}: {
  account: AdminBusinessEntityCurrencycloudAccountFragment;
  businessEntityId: string;
}) {
  const [createCurrencycloudAssets] = useMutation(
    graphql(`
      mutation AdminCreateCurrencycloudAssets($input: CreateCurrencycloudAssetsInput!) {
        createCurrencycloudAssets(input: $input) {
          businessEntity {
            id
            ...AdminBusinessEntityDetail
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {
      currencies: [] as Currency[],
    } as CreateCurrenycloudAssetsFormData,
    validators: {
      onSubmit: createCurrencycloudAssetsSchema,
      onChange: createCurrencycloudAssetsSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = createCurrencycloudAssetsSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating Visa FX assets",
        });
        return;
      }

      try {
        await createCurrencycloudAssets({
          variables: {
            input: {
              currencies: parsed.data.currencies,
              businessEntityId,
            },
          },
        });

        toast({
          kind: "success",
          autoHide: true,
          title: "Visa FX assets created successfully",
        });
        form.reset();
      } catch (error) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to create Visa FX assets",
          description:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
      }
    },
  });

  const openAccounts = account.assets ?? [];
  const accountByCurrency = new Map(openAccounts.map((a) => [a.currency, a]));

  const selectedCurrencies = useStore(form.store, (state) => {
    return state.values.currencies;
  });

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField name="currencies">
          {(field) => (
            <div className="flex flex-wrap">
              {fxAllowedCurrencies.map((currency) => {
                const openAccount = accountByCurrency.get(currency);
                const alreadyExists = !!openAccount;

                const isSelected =
                  field.state.value?.includes(currency) || alreadyExists;

                return (
                  <div
                    key={currency}
                    className="flex w-1/2 items-center gap-4 p-1 pt-2 pr-4 pb-2 pl-4"
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={alreadyExists}
                      onCheckedChange={() => {
                        const current = field.state.value || [];
                        if (current.includes(currency)) {
                          field.handleChange(
                            current.filter((c) => c !== currency),
                          );
                        } else {
                          field.handleChange([...current, currency]);
                        }
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <CurrencyFlagEmoji
                        className="h-4 rounded-xs shadow-surface"
                        currency={currency}
                      />
                      <div className="flex min-h-8 items-center">
                        <span className="text-sm">
                          {currencyCodeToSingularName[currency]} ({currency})
                        </span>
                      </div>
                    </div>

                    {openAccount && (
                      <Link to={`/assets/${openAccount.id}`}>
                        {openAccount.currencycloudBalance ? (
                          <LabelPill
                            variant="success"
                            label={`Account open, balance ID ${openAccount.currencycloudBalance.id}`}
                          />
                        ) : (
                          <LabelPill
                            variant="warning"
                            label="Account open, no balance ID"
                          />
                        )}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </form.AppField>

        <CardFooter className="mt-4 flex justify-end">
          <form.Subscribe
            selector={(state) => ({ isSubmitting: state.isSubmitting })}
          >
            {({ isSubmitting }) => (
              <div>
                <Button
                  variant="primary"
                  disabled={isSubmitting || selectedCurrencies.length === 0}
                  loading={isSubmitting}
                  type="submit"
                >
                  Open Visa FX accounts
                </Button>
              </div>
            )}
          </form.Subscribe>
        </CardFooter>
      </form>
    </form.AppForm>
  );
}

function CurrencycloudLinkSection({
  businessEntityId,
  currencycloudAccounts,
  requirements,
}: {
  businessEntityId: string;
  currencycloudAccounts:
    | AdminAvailableCurrencycloudAccountFragment[]
    | undefined
    | null;
  requirements: CurrencycloudRequirement[];
}) {
  const unlinkedCurrencycloudAccounts = (currencycloudAccounts ?? []).filter(
    (e) => e.businessEntity === null,
  );

  const [currencycloudAccountId, setCurrencycloudAccountId] = useState("");
  const [overrideEligibility, setOverrideEligibility] = useState(false);

  const requirementsMet = requirements.every((requirement) => requirement.met);

  const [linkCurrencycloudAccount, linkCurrencycloudAccountResult] =
    useMutation(
      graphql(`
        mutation LinkCurrencycloudAccount($input: LinkCurrencycloudAccountInput!) {
          linkCurrencycloudAccount(input: $input) {
            businessEntity {
              id
              ...AdminBusinessEntityDetail
            }
          }
        }
      `),
      {
        onCompleted: () => {
          setCurrencycloudAccountId("");
          toast({
            kind: "success",
            autoHide: true,
            title: "Visa account linked successfully",
          });
        },
        onError: (error) => {
          const description =
            error.graphQLErrors[0].message ??
            "Something went wrong. Please try again later.";
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to link Visa account",
            description,
          });
        },
      },
    );

  const handleAdd = () => {
    if (!currencycloudAccountId.trim()) {
      throw new Error("Ebury Client ID is required");
    }
    linkCurrencycloudAccount({
      variables: {
        input: {
          businessEntityId,
          currencycloudAccountId: currencycloudAccountId.trim(),
          overrideEligibilityRequirements: overrideEligibility,
        },
      },
    });
  };

  const blockedByRequirements = !requirementsMet && !overrideEligibility;

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-primary p-4 text-sm shadow-surface">
      <div className="font-semibold">Link existing Visa account</div>

      <CurrencycloudRequirements requirements={requirements} />
      {!requirementsMet && (
        <label className="flex items-center gap-2">
          <Checkbox
            checked={overrideEligibility}
            onCheckedChange={(checked) =>
              setOverrideEligibility(checked === true)
            }
          />
          Override requirements and link account anyway
        </label>
      )}

      {currencycloudAccounts === undefined ? (
        <div className="flex items-center gap-2 text-xs">
          <Spinner size="md" />
          Loading available Visa accounts
        </div>
      ) : (
        <div className="flex gap-4">
          <Select
            disabled={
              unlinkedCurrencycloudAccounts.length === 0 ||
              blockedByRequirements ||
              linkCurrencycloudAccountResult.loading
            }
            onValueChange={setCurrencycloudAccountId}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  unlinkedCurrencycloudAccounts.length
                    ? "Select Visa account"
                    : "No available Visa accounts to link"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {unlinkedCurrencycloudAccounts.map((acc) => (
                <SelectItem key={acc.id} value={acc.id}>
                  {acc.shortReference} - {acc.accountName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={handleAdd}
            disabled={
              linkCurrencycloudAccountResult.loading ||
              blockedByRequirements ||
              !currencycloudAccountId.trim()
            }
            loading={linkCurrencycloudAccountResult.loading}
            className="mb-[1px]"
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

function MercuryOnboardingSection({
  businessEntity,
}: {
  businessEntity: AdminBusinessEntityDetailMercuryOnboardingFragment;
}) {
  const hasExistingConnection = businessEntity.mercuryConnections.some(
    (c) =>
      c.status === "ACTIVE" ||
      c.status === "INITIALISING" ||
      c.status === "RECONNECT_REQUIRED",
  );

  let message: string | undefined;
  if (hasExistingConnection) {
    message = "This entity already has a Mercury connection.";
  } else if (!businessEntity.isUSDomiciled) {
    message = "Mercury onboarding is only available for US-domiciled entities.";
  } else if (businessEntity.address === null) {
    message =
      "An address must be set before Mercury onboarding can be initiated.";
  }

  return (
    <Card>
      {message ? (
        <Txt>{message}</Txt>
      ) : (
        <div className="flex justify-between">
          <Txt>
            Submit onboarding data to open a new Mercury instance on behalf of
            the client.
          </Txt>
          <Link
            to={`/organisations/${businessEntity.organisation?.id}/business-entities/${businessEntity.id}/mercury-onboarding`}
          >
            <Button>Open onboarding form</Button>
          </Link>
        </div>
      )}
    </Card>
  );
}
